import { useRef, useState, type RefObject } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Badge, Box, Button, Card, CardBody, CardHeader, HStack, Input, Modal, ModalOverlay, ModalContent,
  ModalHeader, ModalBody, ModalFooter, Text, VStack } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/contexts/AuthContext'
import { DataTable, type ColumnConfig } from '@/components/common/DataTable'
import { DropdownSelect } from '@/components/common/DropdownSelect'
import { merchantUserInvitationService as service, type UserInvitationEntry,
  type UserReferralEntry } from '@/services/admin/merchantUserInvitationService'
import { UserInvitationPolicies } from './UserInvitationPolicies'
import { useToast } from '@/hooks/useToast'
import { formatClientDateTime } from '@/utils/dateTimeUtils'

export function UserInvitationsTab({ cardBg, refreshContainerRef }: {
  cardBg: string; refreshContainerRef?: RefObject<HTMLDivElement>;
}) {
  const { t } = useTranslation()
  const toast = useToast()
  const { checkPermission } = useAuth()
  const canWrite = checkPermission('system:config')
  const canRead = canWrite || checkPermission('system:read')
  const [userId, setUserId] = useState('')
  const [filter, setFilter] = useState('')
  const [status, setStatus] = useState('all')
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState<UserInvitationEntry | null>(null)
  const [referralPage, setReferralPage] = useState(1)
  const [busy, setBusy] = useState(false)
  const writing = useRef(false)
  const listFeedback = useRef(false)
  const referralFeedback = useRef(false)
  const label = (key: string) => t(`userInvitations.${key}`)
  const errorLabel = (error: unknown) => label(
    (error as { response?: { status?: number } } | null)?.response?.status === 503 ? 'unavailable' : 'failed')
  const list = useQuery({ queryKey: ['admin-user-invitations', filter, status, page], enabled: canRead,
    queryFn: async () => {
      const notify = listFeedback.current
      listFeedback.current = false
      try {
        const data = await service.list({ userId: filter || undefined, status: status === 'all' ? undefined : status,
          limit: 100, offset: (page - 1) * 100 })
        if (notify) toast.success({ title: label('refreshed') })
        return data
      } catch (error) { toast.error({ title: errorLabel(error) }); throw error }
    }, retry: false, gcTime: 0 })
  const referrals = useQuery({ queryKey: ['admin-user-referrals', selected?.userId, referralPage],
    enabled: canRead && Boolean(selected), queryFn: async () => {
      const notify = referralFeedback.current
      referralFeedback.current = false
      try {
        const data = await service.referrals(selected!.userId, (referralPage - 1) * 100)
        if (notify) toast.success({ title: label('refreshed') })
        return data
      } catch (error) { toast.error({ title: errorLabel(error) }); throw error }
    },
    retry: false, gcTime: 0 })
  const changeStatus = async (row: UserInvitationEntry) => {
    if (writing.current || busy || !canWrite) return
    writing.current = true
    setBusy(true)
    try {
      await service.status(row.userId, row.status === 'active' ? 'disabled' : 'active')
      toast.success({ title: label('statusSaved') })
    }
    catch (error) { toast.error({ title: errorLabel(error) }) }
    finally {
      await list.refetch()
      writing.current = false; setBusy(false)
    }
  }
  const refresh = async (kind: 'list' | 'referrals') => {
    const result = await (kind === 'list' ? list.refetch() : referrals.refetch())
    if (!result.isError) toast.success({ title: label('refreshed') })
  }
  const columns: ColumnConfig<UserInvitationEntry>[] = [
    { key: 'userEmail', title: label('user'), renderSubText: row => row.userId },
    { key: 'code', title: label('code') },
    { key: 'status', title: label('codeStatus'), render: (_value, row) =>
      <Badge colorScheme={row.status === 'active' ? 'green' : 'orange'}>{label(row.status)}</Badge> },
    { key: 'userIsActive', title: label('accountStatus'), render: (_value, row) =>
      <Badge colorScheme={row.userIsActive ? 'green' : 'red'}>{label(row.userIsActive ? 'active' : 'disabled')}</Badge> },
    { key: 'successfulInvites', title: label('successful'), render: (_value, row) => String(row.successfulInvites ?? 0) },
    { key: 'createdAt', title: label('created'), render: (_value, row) => formatClientDateTime(row.createdAt) },
  ]
  const referralColumns: ColumnConfig<UserReferralEntry>[] = [
    { key: 'inviteeUserEmail', title: label('user'), renderSubText: row => row.inviteeUserId },
    { key: 'createdAt', title: label('registered'), render: (_value, row) => formatClientDateTime(row.createdAt) },
  ]
  if (!canRead) return <Text>{label('denied')}</Text>
  return <VStack align="stretch" spacing={3}>
    <Card bg={cardBg}><CardBody p={3}><UserInvitationPolicies canWrite={canWrite} refreshContainerRef={refreshContainerRef} /></CardBody></Card>
    <Card bg={cardBg}><CardHeader>
    <HStack spacing={2} flexWrap="wrap"><Input size="sm" maxW="280px" value={userId} onChange={e => setUserId(e.target.value)} placeholder={label('userId')}
      aria-label={label('userId')} /><Button size="sm" onClick={() => {
        if (filter === userId.trim() && page === 1) { void refresh('list'); return }
        listFeedback.current = true; setFilter(userId.trim()); setPage(1)
      }}>{label('filter')}</Button>
      <Box w={{ base: '100%', sm: '200px' }} minW={0}>
        <DropdownSelect size="sm" value={status} ariaLabel={label('codeStatus')} options={['all','active','disabled'].map(value => ({ value, label: label(value) }))}
          onChange={value => { listFeedback.current = value !== status || page !== 1; setStatus(value); setPage(1) }} />
      </Box></HStack>
    </CardHeader><CardBody pt={0}>
    <DataTable data={list.isError ? [] : list.data?.entries ?? []} columns={columns} loading={list.isPending}
      size="sm" emptyMessage={label('empty')} onRefresh={() => void refresh('list')} refreshing={list.isFetching} showRefresh
      pagination={{ page, pageSize: 100, total: list.data?.total ?? 0 }}
      onPageChange={value => { listFeedback.current = value !== page; setPage(value) }}
      actions={row => [
        { label: label('referrals'), onClick: () => { referralFeedback.current = true; setSelected(row); setReferralPage(1) } },
        ...(canWrite ? [{ label: label(row.status === 'active' ? 'disable' : 'enable'), disabled: busy,
          colorScheme: row.status === 'active' ? 'orange' : 'green',
          confirmMessage: row.status === 'active' ? t('userInvitations.confirmDisable', { code: row.code }) : undefined,
          onClick: () => void changeStatus(row) }] : []),
      ]} />
    </CardBody></Card>
    <Modal isOpen={Boolean(selected)} onClose={() => setSelected(null)} size="xl">
      <ModalOverlay /><ModalContent className="system-config-dialog"><ModalHeader>{label('referrals')}</ModalHeader><ModalBody>
        <DataTable data={referrals.isError ? [] : referrals.data?.entries ?? []} columns={referralColumns}
          loading={referrals.isPending} emptyMessage={label('empty')} showRefresh
          size="sm" onRefresh={() => void refresh('referrals')} refreshing={referrals.isFetching}
          pagination={{ page: referralPage, pageSize: 100, total: referrals.data?.total ?? 0 }}
          onPageChange={value => { referralFeedback.current = value !== referralPage; setReferralPage(value) }} />
      </ModalBody><ModalFooter><Button onClick={() => setSelected(null)}>{label('close')}</Button></ModalFooter></ModalContent>
    </Modal>
  </VStack>
}
